import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import numbro from 'numbro';

import { WebPlanTier } from '../../../../constants/allowance';
import {
  CurrencyType,
  getCurrencyDetails,
  StaminaType,
} from '../../../../constants/currencies';
import { PNGIconName } from '../../../../constants/pngIcon';
import { SVGIconType } from '../../../../constants/svgIcon';
import { BalanceType } from '../../../../models/Base';
import { RewardOffer } from '../../../../models/RewardStore';
import {
  AppBar,
  BreadBar,
  CountDownText,
  PngIcon,
  SubscriptionCard,
  SubscriptionCardOverlayTag,
  SvgIcon,
  WebStoreProductNode,
} from '../../../Molecules';
import { AllHorizontalRow } from '../../../Organisms';

import styles from './WalletTemplate.module.css';

type WalletTemplateProps = {
  isTrialUser: boolean;
  tier: WebPlanTier | undefined;
  webProducts: RewardOffer[];
  getCurrencyData: (currencyType: CurrencyType) =>
    | {
        currentAmount: number;
        totalAmount?: number;
        willRefillAt?: Date;
      }
    | undefined;
  onClickSubscription: () => void;
  onClickCurrency: (currencyType: CurrencyType) => void;
  onClickRegister?: () => void;
  onClickProduct: (rewardOfferId: string) => void;
  onClickAllWebStore: () => void;
};

const CurrencyRow = memo(
  ({
    currencyType,
    currentAmount,
    totalAmount,
    willRefillAt,
    icon,
    displayName,
    onClickCurrency,
  }: {
    currencyType: CurrencyType;
    currentAmount?: number;
    totalAmount?: number;
    willRefillAt?: Date;
    icon: PNGIconName;
    displayName: string;
    onClickCurrency: (currencyType: CurrencyType) => void;
  }) => {
    const { t } = useTranslation();

    return (
      <div
        className={styles.currencyRow}
        onClick={() => {
          onClickCurrency(currencyType);
        }}
      >
        <PngIcon name={icon} width={28} height={28} />
        <div className={styles.rowContent}>
          <p>{t(displayName)}</p>
          {willRefillAt ? (
            <div className={styles.refillTextBar}>
              <SvgIcon name={SVGIconType.ClockRefill} width={24} height={24} />
              <CountDownText
                timestamp={willRefillAt.getTime()}
                label={t('Next Refill: ')}
                className={styles.refillText}
              />
            </div>
          ) : null}
        </div>
        <div className={styles.tail}>
          {currencyType === BalanceType.BreadSlice ? (
            <BreadBar breadSlice={currentAmount ?? 0} width="150px" />
          ) : (
            <p>
              {totalAmount
                ? `${currentAmount} / ${totalAmount}`
                : `${numbro(currentAmount).format({
                    thousandSeparated: true,
                  })}`}
            </p>
          )}
          <SvgIcon
            name={SVGIconType.Arrow}
            width={16}
            height={16}
            color="#808080"
          />
        </div>
      </div>
    );
  },
);

export const WalletTemplate = ({
  isTrialUser,
  tier,
  webProducts,
  getCurrencyData,
  onClickCurrency,
  onClickSubscription,
  onClickRegister,
  onClickProduct,
  onClickAllWebStore,
}: WalletTemplateProps) => {
  const { t } = useTranslation();

  return (
    <div className={styles.container}>
      <AppBar
        title={t('Wallet')}
        withBack={true}
        leading={
          <SvgIcon
            name={SVGIconType.Wallet}
            width={28}
            height={28}
            color="#333333"
          />
        }
      />
      <div
        className={styles.list}
        style={{ marginTop: `${!isTrialUser ? '0.8rem' : ''}` }}
      >
        {isTrialUser && (
          <div className={styles.currencySection}>
            <SubscriptionCard
              tier={undefined}
              period={undefined}
              onClickRegister={onClickRegister}
            />
          </div>
        )}

        <div className={`${styles.currencySection} ${styles.currencyGroup}`}>
          {!isTrialUser && (
            <SubscriptionCard
              tier={tier}
              period={undefined}
              onClickSubscription={onClickSubscription}
              overlayTag={SubscriptionCardOverlayTag.Current}
            />
          )}
          <CurrencyRow
            currencyType={StaminaType.GameStamina}
            {...getCurrencyData(StaminaType.GameStamina)}
            {...getCurrencyDetails(StaminaType.GameStamina)}
            onClickCurrency={onClickCurrency}
          />
          <div className={styles.separator}>
            <hr />
          </div>
          <CurrencyRow
            currencyType={StaminaType.CreationStamina}
            {...getCurrencyData(StaminaType.CreationStamina)}
            {...getCurrencyDetails(StaminaType.CreationStamina)}
            onClickCurrency={onClickCurrency}
          />
        </div>

        <div className={styles.currencySection}>
          <div className={styles.currencyGroup}>
            <CurrencyRow
              currencyType={BalanceType.Token}
              {...getCurrencyData(BalanceType.Token)}
              {...getCurrencyDetails(BalanceType.Token)}
              onClickCurrency={onClickCurrency}
            />
          </div>
          {/* <div className={styles.currencyGroup}>
            <CurrencyRow
              currencyType={BalanceType.JoyPoint}
              {...getCurrencyData(BalanceType.JoyPoint)}
              {...getCurrencyDetails(BalanceType.JoyPoint)}
              onClickCurrency={onClickCurrency}
            />
          </div> */}
        </div>

        <div className={styles.currencySection}>
          <div className={styles.currencyGroup}>
            <CurrencyRow
              currencyType={BalanceType.BreadSlice}
              {...getCurrencyData(BalanceType.BreadSlice)}
              {...getCurrencyDetails(BalanceType.BreadSlice)}
              onClickCurrency={onClickCurrency}
            />
          </div>
        </div>
      </div>
      <div
        style={{ marginBottom: '2.5rem', marginTop: '1.8rem', width: '100%' }}
      >
        <AllHorizontalRow
          sectionTitle={'Web Store'}
          items={webProducts}
          onClickAll={onClickAllWebStore}
        >
          {(item: RewardOffer) => (
            <WebStoreProductNode
              rewardOffer={item}
              onClickProduct={() => {
                onClickProduct(item.rewardOfferId);
              }}
            />
          )}
        </AllHorizontalRow>
      </div>
    </div>
  );
};
